import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styles from 'styles/modules/userBox.module.scss';
import { connect } from 'react-redux';
import { closeUserPopup, openUserPopup } from 'actions/ui';
import store from 'store';
import * as api from 'api';
import UserPopup from './UserPopup';

const mapStateToProps = state => ({
	user: state.user,
	isPopupOpen: state.ui.isUserPopupOpen,
	unreadCountQuery: api.mw.internalNotifications.default.endpoints.unreadCount.select()(state),
});

const mapDispatchToProps = dispatch => ({
	closeUserPopup: () => dispatch(closeUserPopup()),
	openUserPopup: () => dispatch(openUserPopup()),
});

@connect(mapStateToProps, mapDispatchToProps)
class UserMenu extends React.Component {
	avatarCacheKey = Date.now();

	constructor(props) {
		super(props);
		this.selectFormRef = React.createRef();
		this.userMenuRef = React.createRef();
		this.isUnreadFetching = false;
		this.unreadTimer = null;
		this.unreadSubscription = null;
	}

	componentDidMount() {
		this.fetchUnreadCount();
		this.unreadTimer = setInterval(this.fetchUnreadCount, 60000);
	}

	componentWillUnmount() {
		if (this.unreadTimer) {
			clearInterval(this.unreadTimer);
		}
		if (this.unreadSubscription?.unsubscribe) {
			this.unreadSubscription.unsubscribe();
		}
	}

	getPhotoSource = () => {
		const { user_photo } = this.props.user;

		if (!user_photo) {
			return '';
		}

		const normalized = String(user_photo).trim();
		const isJsonPayload = normalized.startsWith('{') || normalized.startsWith('[');

		if (normalized.startsWith('data:image')) {
			if (normalized.includes('{') || isJsonPayload) {
				return `/mw/file/avatar?v=${this.avatarCacheKey}`;
			}
			return normalized;
		}

		if (isJsonPayload) {
			return `/mw/file/avatar?v=${this.avatarCacheKey}`;
		}

		if (normalized.startsWith('http') || normalized.startsWith('/') || normalized.startsWith('mw/')) {
			return normalized;
		}

		return `data:image/png;base64,${normalized}`;
	};

	fetchUnreadCount = () => {
		if (this.isUnreadFetching) {
			return;
		}

		this.isUnreadFetching = true;

		const request = this.unreadSubscription?.refetch
			? this.unreadSubscription.refetch()
			: store.dispatch(
				api.mw.internalNotifications.unreadCount.initiate(undefined, {
					forceRefetch: true,
				}),
			);

		if (!this.unreadSubscription && request?.unsubscribe) {
			this.unreadSubscription = request;
		}

		request
			.catch(() => {})
			.finally(() => {
				this.isUnreadFetching = false;
			});
	};

	getUnreadCounts = () => {
		const payload = this.props.unreadCountQuery?.data;

		if (!payload?.success) {
			return {
				total: 0,
				general: 0,
				knowledgeBaseUpdate: 0,
			};
		}

		const payloadResult = payload.result || {};
		const general = Number(payloadResult.general || 0);
		const knowledgeBaseUpdate = Number(payloadResult.knowledge_base_update || 0);
		const total = Number(payloadResult.total ?? (general + knowledgeBaseUpdate));

		return {
			total,
			general,
			knowledgeBaseUpdate,
		};
	};

	getUnreadDisplay = () => {
		const { total } = this.getUnreadCounts();

		if (!total) {
			return null;
		}

		if (total > 99) {
			return '99+';
		}

		return total;
	};

	handleClickOutside = (e) => {
		if (!this.userMenuRef.current.contains(e.target)
            && !this.selectFormRef.current.contains(e.target)
            && !e.target.classList.contains('Select-option')
            && !e.target.classList.contains('Select-clear-zone')
            && !e.target.classList.contains('Select-clear')
		) {
			this.props.closeUserPopup();
		}
	};

	getHeaderAvatar = () => {
		const unreadDisplay = this.getUnreadDisplay();
		const unreadTitle = unreadDisplay
			? `Повідомлення: ${unreadDisplay}`
			: 'Повідомлення';

		if (this.props.user.user_photo) {
			return (
				<div className={styles.avatarWrapper}>
					<img
						alt=""
						className={styles.userImage}
						src={this.getPhotoSource()}
					/>
					{unreadDisplay && (
						<div className={styles.notificationBell} title={unreadTitle}>
							<svg
								className={styles.notificationBellIcon}
								viewBox="0 0 24 24"
								aria-hidden="true"
								focusable="false"
							>
								<path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
							</svg>
							<span className={styles.notificationBellCount}>{unreadDisplay}</span>
						</div>
					)}
				</div>
			);
		}

		return (
			<div className="circle-wrapper">
				<div className="icon-user-wrapper">
					<i className="icon-user" />
				</div>
				{unreadDisplay && (
					<div className={styles.notificationBell} title={unreadTitle}>
						<svg
							className={styles.notificationBellIcon}
							viewBox="0 0 24 24"
							aria-hidden="true"
							focusable="false"
						>
							<path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
						</svg>
						<span className={styles.notificationBellCount}>{unreadDisplay}</span>
					</div>
				)}
			</div>
		);
	};

	render() {
		const {
			user: { login }, isPopupOpen, openUserPopup, closeUserPopup,
		} = this.props;

		return (
			<div className={styles.userBoxWrapper}>

				<div
					className={cx('header-block-item', styles.userBox)}
					ref={this.userMenuRef}
					onClick={isPopupOpen ? closeUserPopup : openUserPopup}
				>
					{this.getHeaderAvatar()}
					<span className={styles.userText}>{login}</span>
				</div>

				{
					isPopupOpen && (
						<UserPopup
							formRef={this.selectFormRef}
							user={this.props.user}
							handleClickOutside={this.handleClickOutside}
							onClose={closeUserPopup}
						/>
					)
				}
			</div>
		);
	}
}

UserMenu.propTypes = {
	user: PropTypes.object,
	onUserMenuClick: PropTypes.func,
};

export default UserMenu;
